import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Lock, Mail, Building2, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import Logo from '../components/Logo';
import api, { setToken } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { scorePassword } from '../utils/passwordStrength';

/**
 * Wave 1u8 — Activation page for a freshly-created tenant.
 *
 * Flow:
 *   1. Superadmin POSTs /api/superadmin/tenants → sends an email with
 *      ?token=…
 *   2. Recipient lands here → GET /api/auth/tenant-onboarding/info to pre-fill
 *   3. User picks a name + password → POST .../verify → session issued
 *   4. Redirect to /app/dashboard inside their NEW tenant
 */
export default function TenantOnboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const token = params.get('token');

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [info, setInfo] = useState(null);  // {tenant_name, contact_email, contact_name, expires_at}
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadingInfo(false);
      setError('Aucun token dans l\'URL. Demandez à votre administrateur un nouveau lien.');
      return;
    }
    api.get('/auth/tenant-onboarding/info', { params: { token } })
      .then(({ data }) => {
        setInfo(data);
        setName(data?.contact_name || '');
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Lien invalide ou expiré');
      })
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Indiquez votre prénom et nom.'); return; }
    if (password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (password !== confirm) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    const { common, score } = scorePassword(password);
    if (common) { setError('Ce mot de passe est trop courant — choisissez-en un autre.'); return; }
    if (score < 2) { setError('Mot de passe trop faible. Ajoutez des majuscules, chiffres ou caractères spéciaux.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/tenant-onboarding/verify', {
        token, password, name: name.trim(),
      });
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      setDone(true);
      setTimeout(() => navigate(data.redirect || '/app/dashboard', { replace: true }), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Activation impossible');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-7 h-7 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-[440px] px-6">
          <div className="w-16 h-16 rounded-2xl bg-[#E6FBF7] flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-[#0F766E]" />
          </div>
          <h1 className="mt-5 text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Bienvenue sur OFManager !
          </h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]">
            Votre espace <strong>{info?.tenant_name}</strong> est prêt. Redirection en cours…
          </p>
        </div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="max-w-[420px] text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h1 className="mt-4 text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Activation impossible</h1>
          <p className="mt-2 text-[13.5px] text-[#6B6C85]" data-testid="onboarding-error">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="onboarding-back-to-login"
            className="mt-5 h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-10">
      <div className="w-full max-w-[460px]">
        <div className="mb-8" data-testid="onboarding-brand">
          <Logo size={48} variant="full" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10.5px] font-bold uppercase tracking-wider mb-4">
          <Sparkles className="w-3 h-3" /> Activation
        </div>
        <h1 className="text-[26px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Bienvenue dans votre espace<br /><span className="text-[#4F46E5]">{info?.tenant_name}</span>
        </h1>
        <p className="text-[13.5px] text-[#6B6C85] mt-3 mb-6">
          Plus qu'une étape pour activer votre compte administrateur. Choisissez un mot de passe sécurisé d'au moins 8 caractères.
        </p>

        {info?.contact_email && (
          <div className="flex items-center gap-2 text-[12.5px] text-[#6B6C85] mb-4 px-3 py-2 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
            <Mail className="w-3.5 h-3.5" />
            <span className="truncate" data-testid="onboarding-email">{info.contact_email}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="onboarding-form">
          <div className="relative">
            <Building2 className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="onboarding-name"
              type="text"
              placeholder="Votre prénom + nom"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="onboarding-password"
              type="password"
              placeholder="Nouveau mot de passe"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          <PasswordStrengthMeter value={password} />
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
            <input
              data-testid="onboarding-password-confirm"
              type="password"
              placeholder="Confirmer le mot de passe"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
          </div>
          {error && (
            <div data-testid="onboarding-form-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
          )}
          <button
            data-testid="onboarding-submit"
            type="submit"
            disabled={submitting}
            className="w-full h-11 rounded-xl brand-gradient text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Activer mon espace
          </button>
        </form>

        {info?.expires_at && (
          <p className="text-[11px] text-[#8E8FA5] mt-4 text-center">
            Ce lien expire le {new Date(info.expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}.
          </p>
        )}
      </div>
    </div>
  );
}
